import { useEffect } from "react";
import { Badge, Col, Collapse, Form, Row } from "react-bootstrap";
import { MdAccessTime, MdLocalPhone, MdOutlineMailOutline, MdOutlineTextFields } from "react-icons/md";
import { IoIosList, IoMdLink } from "react-icons/io";
import { BsCalendar2Date, BsCalendar2Month, BsTextareaResize } from "react-icons/bs";
import { GoNumber } from "react-icons/go";
import { LuCalendarClock, LuFileChartColumnIncreasing } from "react-icons/lu";
import { PiPasswordBold } from "react-icons/pi";
import { FaXmark } from "react-icons/fa6";
import { FaRegPlusSquare, FaTrashAlt } from "react-icons/fa";

const FormEditor = ({
    setOpenList,
    openList,
    setFieldList,
    fieldList,
    AppRef,
    formMetadata
}) => {

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (AppRef.current && !AppRef.current.contains(event.target)) {
                setOpenList(false);
            }
        };
        document.addEventListener("click", handleClickOutside, true);
        return () => {
            document.removeEventListener("click", handleClickOutside, true);
        };
    }, []);

    const addField = (item) => {
        setFieldList(prev => [
            ...prev,
            {
                id: Math.random().toString().substr(2, 10),
                label: "",
                name: "",
                placeholder: "",
                value: "",
                optionId: "",
                options: [],
                type: item?.type,
                define: "pre",
                require: true,
                isEncrypted: true,
            }
        ]);
    };

    const handleChange = (id, key, value) => {
        setFieldList(prev =>
            prev.map(f => {
                if (f.id !== id) return f;
                if (key === "optionId") {
                    return {
                        ...f,
                        optionId: value,
                        options: []
                    };
                }
                return {
                    ...f,
                    [key]: value
                };
            })
        );
    };


    const removeField = (id) => {
        setFieldList(prev => prev.filter(f => f.id !== id));
    };

    const addOption = (fieldId) => {
        setFieldList(prev =>
            prev.map(f =>
                f.id === fieldId
                    ? { ...f, options: [...f.options, { id: crypto.randomUUID(), name: "", key: "" }] }
                    : f
            )
        );
    };

    const removeOption = (fieldId, optionId) => {
        setFieldList(prev =>
            prev.map(f =>
                f.id === fieldId
                    ? { ...f, options: f.options.filter(opt => opt.id !== optionId) }
                    : f
            )
        );
    };

    const handleOptionChange = (fieldId, optionId, key, value) => {
        setFieldList(prev =>
            prev.map(f =>
                f.id === fieldId
                    ? {
                        ...f,
                        options: f.options.map(opt =>
                            opt.id === optionId ? { ...opt, [key]: value } : opt
                        )
                    }
                    : f
            )
        );
    };

    const toFieldName = (value = "") => {
        return String(value)
            .trim()
            .toUpperCase()
            .replace(/[^A-Z0-9 ]/g, " ")
            .replace(/\s+/g, "_")
            .replace(/_+/g, "_")
            .replace(/^_+|_+$/g, "");
    };

    const fieldTypes = [
        { type: "text", label: "Text", icon: <MdOutlineTextFields /> },
        { type: "url", label: "URL", icon: <IoMdLink /> },
        { type: "email", label: "Email", icon: <MdOutlineMailOutline /> },
        { type: "textarea", label: "Textarea", icon: <BsTextareaResize /> },
        { type: "date", label: "Date", icon: <BsCalendar2Date /> },
        { type: "month", label: "Month/Year", icon: <BsCalendar2Month /> },
        { type: "time", label: "Time", icon: <MdAccessTime /> },
        { type: "datetime-local", label: "Date-Time", icon: <LuCalendarClock /> },
        { type: "phone", label: "Phone", icon: <MdLocalPhone /> },
        { type: "number", label: "Number", icon: <GoNumber /> },
        { type: "file", label: "File", icon: <LuFileChartColumnIncreasing /> },
        { type: "password", label: "Password", icon: <PiPasswordBold /> },
        { type: "select", label: "Select", icon: <IoIosList /> },
    ];

    return (
        <>
            <Form.Group ref={AppRef}>
                <Collapse in={openList}>
                    <div className={`mb-2 bg-light border p-3`} style={{ borderRadius: "0px 0px 5px 5px" }}>
                        <div className="d-flex gap-2 flex-wrap">
                            {fieldTypes?.map(item => (
                                <button
                                    key={item?.type}
                                    action
                                    style={{ width: 100 }}
                                    title={item?.label}
                                    className={`category-card p-2 bg-light`}
                                    onClick={() => addField(item)}
                                >
                                    <span style={{ fontSize: 16 }}>{item.icon}</span>
                                    <span style={{ fontSize: 11 }}>{item.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </Collapse>
            </Form.Group>
            <div className="px-4 py-2">
                {fieldList?.map(field => (
                    <Row key={field.id} className="mb-4 mt-2 bg-light border position-relative">
                        <Form.Group as={Col} md={6} className="mb-2 mt-3">
                            <Form.Label className="fw-bold mb-1" style={{ fontSize: 13 }}>
                                Label{" "}
                                <abbr className="text-danger" title="Require">
                                    *
                                </abbr>
                            </Form.Label>
                            <Form.Control
                                type="text"
                                className="interactive-input"
                                style={{ fontSize: 12, padding: "6px 15px" }}
                                placeholder="Enter Label"
                                value={field.label}
                                disabled={formMetadata?.lockStatus}
                                onChange={(e) => {
                                    handleChange(field.id, "label", e.target.value);
                                    handleChange(field.id, "name", toFieldName(e.target.value))
                                }}
                            />
                        </Form.Group>
                        <Form.Group as={Col} md={6} className="mb-2 mt-3">
                            <Form.Label className="fw-bold mb-1" style={{ fontSize: 13 }}>
                                Name{" "}
                                <abbr className="text-danger" title="Require">
                                    *
                                </abbr>
                            </Form.Label>
                            <Form.Control
                                type="text"
                                disabled={formMetadata?.lockStatus}
                                className="interactive-input"
                                style={{ fontSize: 12, padding: "6px 15px" }}
                                placeholder="Enter Name"
                                value={toFieldName(field.name)}
                            />
                        </Form.Group>
                        <Form.Group as={Col} md={12} className="mb-2">
                            <Form.Label className="fw-bold mb-1" style={{ fontSize: 13 }}>
                                Placeholder{" "}
                                <abbr className="text-danger" title="Require">
                                    *
                                </abbr>
                            </Form.Label>
                            <Form.Control
                                type="text"
                                disabled={formMetadata?.lockStatus}
                                className="interactive-input"
                                style={{ fontSize: 12, padding: "6px 15px" }}
                                placeholder="Enter Placeholder"
                                value={field.placeholder}
                                onChange={(e) => handleChange(field.id, "placeholder", e.target.value)}
                            />
                        </Form.Group>
                        {field.type === "select" &&
                            <Form.Group as={Col} md={12} className="mb-2">
                                <Form.Label className="fw-bold mb-1" style={{ fontSize: 13 }}>
                                    Options Id{" "}
                                    <abbr className="text-danger" title="Require">
                                        *
                                    </abbr>
                                </Form.Label>
                                <Form.Select
                                    className="interactive-input"
                                    value={field.optionId}
                                    disabled={formMetadata?.lockStatus}
                                    onChange={(e) =>
                                        handleChange(field.id, "optionId", e.target.value)
                                    }
                                    style={{ fontSize: 12, padding: "6px 15px" }}
                                >
                                    <option value="">Select Option Id</option>
                                    <option value="custom">Custom Option</option>
                                    <option value="bankaccount">Bank List</option>
                                    <option value="creditdebit">Card List</option>
                                    <option value="database">Database List</option>
                                    <option value="storage">Storage List</option>
                                    <option value="file">File List</option>
                                </Form.Select>
                            </Form.Group>
                        }
                        {field.type === "select" && field.optionId === "custom" && (
                            <Col md={12} className="mb-3">
                                <div className="border rounded p-2 bg-white">
                                    <div className="hstack gap-2 align-items-center mb-2">
                                        <strong style={{ fontSize: 13 }}>Options</strong>
                                        {!formMetadata?.lockStatus &&
                                            <FaRegPlusSquare className="text-primary CP" onClick={() => addOption(field.id)} />
                                        }
                                    </div>
                                    {field.options?.map((opt, index) => (
                                        <Row key={index} className="align-items-center mb-2">
                                            <Col md={6}>
                                                <Form.Control
                                                    size="sm"
                                                    disabled={formMetadata?.lockStatus}
                                                    placeholder="Label"
                                                    style={{ fontSize: 12, padding: "6px 15px" }}
                                                    value={opt.name}
                                                    onChange={(e) =>
                                                        handleOptionChange(field.id, opt.id, "name", e.target.value)
                                                    }
                                                />
                                            </Col>
                                            <Col md={6} className="hstack gap-2">
                                                <Form.Control
                                                    size="sm"
                                                    disabled={formMetadata?.lockStatus}
                                                    placeholder="Value"
                                                    style={{ fontSize: 12, padding: "6px 15px" }}
                                                    value={opt.key}
                                                    onChange={(e) =>
                                                        handleOptionChange(field.id, opt.id, "key", e.target.value)
                                                    }
                                                />
                                                {!formMetadata?.lockStatus &&
                                                    <FaTrashAlt onClick={() => removeOption(field.id, opt.id)} className="text-danger CP" style={{ fontSize: 12 }} />
                                                }
                                            </Col>
                                        </Row>
                                    ))}
                                    {field.options.length === 0 && (
                                        <div className="text-muted" style={{ fontSize: 12 }}>
                                            No options added
                                        </div>
                                    )}
                                </div>
                            </Col>
                        )}
                        <Form.Group as={Col} md={6} className="my-2 hstack gap-2">
                            <Form.Label className="fw-bold mb-0" style={{ fontSize: 13 }}>
                                Require{" "}
                                <abbr className="text-danger" title="Require">
                                    *
                                </abbr>
                            </Form.Label>
                            <Form.Check
                                type="switch"
                                id={`switch-${field.id}`}
                                disabled={formMetadata?.lockStatus}
                                checked={field.require}
                                style={{ fontSize: 12 }}
                                onChange={(e) =>
                                    handleChange(field.id, "require", e.target.checked)
                                }
                            />
                        </Form.Group>
                        <Form.Group as={Col} md={6} className="my-2 hstack gap-2">
                            <Form.Label className="fw-bold mb-0" style={{ fontSize: 13 }}>
                                isEncrypted{" "}
                                <abbr className="text-danger" title="Require">
                                    *
                                </abbr>
                            </Form.Label>
                            <Form.Check
                                type="switch"
                                id={`switch-${field.id}`}
                                checked={field.isEncrypted}
                                style={{ fontSize: 12 }}
                                disabled={formMetadata?.lockStatus}
                                onChange={(e) =>
                                    handleChange(field.id, "isEncrypted", e.target.checked)
                                }
                            />
                        </Form.Group>
                        <Badge
                            className="text-capitalize position-absolute top-0 start-0 rounded-1 translate-middle-y ms-2"
                            style={{ width: field?.type === "datetime-local" ? "auto" : 80 }}
                        >
                            {field?.type}
                        </Badge>
                        {!formMetadata?.lockStatus &&
                            <Badge
                                bg="danger"
                                onClick={() => removeField(field?.id)}
                                className="text-white position-absolute top-0 end-0 rounded CP translate-middle-y ms-2"
                                style={{ width: 25 }}
                            >
                                <FaXmark />
                            </Badge>
                        }
                    </Row>
                ))}
            </div>
        </>

    );
};

export default FormEditor;
